import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { TESTIDS } from "@/lib/testIds";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { user, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Sign in | ImpactVest";
  }, []);

  useEffect(() => {
    if (user) navigate("/app");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      login(res.data.token, res.data.user);
      toast.success("Signed in.");
      window.location.assign("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Header />
      <main className="pt-28 md:pt-32 pb-24">
        <div className="iv-container">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 lg:col-span-5">
              <div className="iv-eyebrow mb-8">Sign in</div>
              <h1 className="font-serif-display text-[48px] md:text-[64px] leading-[0.98] tracking-tight text-iv-fg">
                Sign in to<br />ImpactVest.
              </h1>
              <p className="mt-6 text-[15.5px] leading-relaxed text-iv-fg-2 max-w-md">
                Access your private workspace to review evidence, generated
                Decision Passports and completed analyses.
              </p>
              <p
                data-testid={TESTIDS.login.notice}
                className="mt-8 font-mono text-[11px] tracking-[0.14em] uppercase text-iv-fg-3 max-w-md"
              >
                For invited organizational workspaces. Trying ImpactVest for the first time? Start with the Test Workspace instead.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/access" className="iv-btn iv-btn--primary">
                  Enter the Test Workspace
                </Link>
                <Link to="/sample" className="iv-btn">
                  Try the guided sample
                </Link>
              </div>
            </div>

            <form
              data-testid={TESTIDS.login.form}
              onSubmit={submit}
              className="col-span-12 lg:col-span-6 lg:col-start-7 iv-panel p-8 md:p-10 self-start"
            >
              <div className="flex items-center justify-between mb-8">
                <span className="font-mono-tag text-iv-fg-3">Secure Access</span>
                <span className="iv-chip iv-chip--gold">Private workspace</span>
              </div>
              <div className="space-y-8">
                <label className="iv-field">
                  <span>Professional Email</span>
                  <input
                    data-testid={TESTIDS.login.email}
                    required
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@organization.com"
                  />
                </label>
                <label className="iv-field">
                  <span>Password</span>
                  <input
                    data-testid={TESTIDS.login.password}
                    required
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="The password you set for your workspace"
                  />
                </label>
              </div>
              <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid={TESTIDS.login.submit}
                  className="iv-btn iv-btn--primary"
                >
                  {submitting ? "Signing in…" : "Sign In"}
                </button>
                <span className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-iv-fg-3">
                  Encrypted · Session-based
                </span>
              </div>
            </form>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
